import React from 'react'

export interface TitleOverlayProps {
  /** The programme. Rendered on the first line, in the heavier weight. */
  series?: string
  /** The instalment. Rendered beneath the series, lighter and smaller. */
  episode?: string
  /**
   * The admin-facing identifier, used ONLY as a fallback. Series and episode
   * are the player-facing title; `name` is what the video list and the admin
   * page heading call this record. An older video predating those two fields
   * has nothing else to say what it is, so it says this rather than nothing.
   */
  name?: string
  /**
   * The player's `uiFadeStyle`. The title appears and fades WITH the control
   * bar — Gavin, 9 Sep 2026: "when you press pause or you bring up the play
   * bar it shows in the top left-hand corner the title of the programme." It
   * is handed the same object rather than deriving its own visibility, so the
   * two can never disagree about whether the UI is up.
   */
  fadeStyle?: React.CSSProperties
}

// One line of the title. Truncates rather than wrapping: a long series name
// must not push the episode line down the picture, and two lines is the
// agreed shape (decision 5).
const lineStyle: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Over live video, so it carries its own contrast rather than trusting the
  // frame underneath. The controls overlay's gradient does not reach up here.
  textShadow: '0 1px 4px rgba(0,0,0,0.85)',
}

/**
 * The programme title, top-left inside the player.
 *
 * Renders nothing at all when it has nothing to say — no empty box, no
 * placeholder — so a video with none of the three fields set looks exactly as
 * it did before this component existed.
 */
const TitleOverlay: React.FC<TitleOverlayProps> = ({ series, episode, name, fadeStyle }) => {
  const seriesText = series?.trim()
  const episodeText = episode?.trim()
  const fallback = seriesText || episodeText ? '' : name?.trim()

  const first = seriesText || episodeText || fallback
  if (!first) return null
  // The second line exists only when BOTH real fields are set. With one of
  // them, whichever it is has already been used as the single line above.
  const second = seriesText && episodeText ? episodeText : ''

  return (
    <div
      // Not a control: it must never intercept a click meant for the surface
      // (which toggles playback) or for a camera drag.
      aria-hidden={false}
      style={{
        position: 'absolute',
        top: '10px',
        left: '12px',
        // Above the surface and its source-swap cover, below the podcast
        // transcript (9), the controls (10), the failure scrim (11) and the
        // verification overlay (12).
        zIndex: 6,
        pointerEvents: 'none',
        // Leaves the top-right cluster (compass and camera controls) clear.
        maxWidth: '55%',
        color: '#fff',
        ...fadeStyle,
      }}
    >
      <div style={{ ...lineStyle, fontSize: '17px', fontWeight: 700, lineHeight: 1.3 }}>
        {first}
      </div>
      {second && (
        <div style={{ ...lineStyle, fontSize: '14px', fontWeight: 400, opacity: 0.85, lineHeight: 1.3 }}>
          {second}
        </div>
      )}
    </div>
  )
}

TitleOverlay.displayName = 'TitleOverlay'
export default TitleOverlay
